import { Navigate, useLocation } from 'react-router-dom';
import React from 'react';
import { AuthContext } from './AuthContext';
import LocalStorageService from 'src/shared/services/localStorageService';
import sidebarConfig from 'src/Layouts/dashboard/navbar/NavConfig';

 
export const PrivateRoute = ({ children, menuid = 0, routeType = 0 }) => {
  const { authUser, authStatusReported } = React.useContext(AuthContext);
  const location = useLocation();

  // while auth state is being determined, render nothing
  if (!authStatusReported) return null;

  // not logged in -> go to login
  if (!authUser?.isLoggedIn) return <Navigate to="/login" replace />;

  // gather roles from authData (local storage) - this is the source of truth
  const userRoles = new LocalStorageService().authData?.roles || [];

  // helper: case-insensitive match between user roles and required roles
  const hasRoleAccess = (userRolesArg, requiredRolesArg) => {
    if (!requiredRolesArg || requiredRolesArg.length === 0) return true;
    const normalizedUserRoles = (Array.isArray(userRolesArg) ? userRolesArg : [userRolesArg]).map((r) => String(r).toLowerCase());
    return requiredRolesArg.some((r) => normalizedUserRoles.includes(String(r).toLowerCase()));
  };

  const findRolesByMenuId = (menuidArg) => {
    const idStr = String(menuidArg);

    const traverse = (items, parentRoles) => {
      for (const item of items) {
        const effectiveRoles = item.roles || parentRoles || [];
        if (item?.menuid && String(item.menuid) === idStr) return effectiveRoles;
        if (item?.children) {
          const res = traverse(item.children, effectiveRoles);
          if (res && res.length >= 0) return res;
        }
      }
      return null;
    };

    for (const section of sidebarConfig) {
      const items = section.items || [];
      const r = traverse(items, []);
      if (r) return r;
    }
    return [];
  };

  // helper: find roles by path from NavConfig (search recursively)
  const findRolesByPath = (pathArg) => {
    const traverse = (items, parentRoles) => {
      for (const item of items) {
        const effectiveRoles = item.roles || parentRoles || [];
        if (item?.path && item.path === pathArg) return effectiveRoles;
        if (item?.children) {
          const res = traverse(item.children, effectiveRoles);
          if (res && res.length >= 0) return res;
        }
      }
      return null;
    };

    for (const section of sidebarConfig) {
      const items = section.items || [];
      const r = traverse(items, []);
      if (r) return r;
    }
    return [];
  };

  // admin override: admins can access everything
  if (hasRoleAccess(userRoles, ['Admin'])) return children;

  // allow if menuid === -1 (special public/dashboard route) and logged in
  if (menuid === -1 && authUser.isLoggedIn) return children;

  // get required roles for this menuid from NavConfig
  let requiredRoles = findRolesByMenuId(menuid);

  // if no roles found by menuid, try to find by current path
  if ((requiredRoles || []).length === 0) {
    const pathRoles = findRolesByPath(location.pathname);
    if (pathRoles && pathRoles.length > 0) requiredRoles = pathRoles;
  }

  // if no role restrictions (not specified in NavConfig), allow access
  if (requiredRoles.length === 0) return children;

  // check if user has one of the required roles for this menuid
  if (hasRoleAccess(userRoles, requiredRoles)) return children;

  // otherwise deny access
  return <Navigate to="/" replace />;
};
 
